import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useToast } from '../../components/ui/Toast';
import Shell from '../dashboard/Shell';
import { Copy, Mail, AlertTriangle, Check, X, Edit2 } from 'lucide-react';
import { fetchUserProfile, updateUserProfile, changePassword } from './profileSlice';
import { updateStore, fetchMyStore } from '../store/storeSlice';
import './Profile.css';

const getInitials = (firstName, lastName, username, email) => {
  const first = (firstName || '').trim()[0];
  const last = (lastName || '').trim()[0];
  if (first && last) return (first + last).toUpperCase();
  if (first) return first.toUpperCase();
  const fromUsername = (username || '').trim();
  if (fromUsername) return fromUsername.slice(0, 2).toUpperCase();
  const fromEmail = (email || '').trim();
  if (fromEmail) return fromEmail.slice(0, 2).toUpperCase();
  return '··';
};

export default function Profile() {
  const dispatch = useDispatch();
  const { user: authUser, getAxios, refreshUser } = useSecureAuth();
  const toast = useToast();
  const store = useSelector((s) => s.store.current);
  const { status, error } = useSelector((s) => s.profile);
  const user = authUser;

  const [isEditing, setIsEditing] = useState(false);
  const [emailVerificationPending, setEmailVerificationPending] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    whatsappNumber: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
  });

  // Load store data + refresh auth-context user so post-session fields
  // (e.g. merchantCode added after login) reach the dashboard without
  // forcing a re-login.
  useEffect(() => {
    if (user) {
      const axios = getAxios();
      dispatch(fetchMyStore(axios));
      refreshUser?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    setFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      username: user.username || '',
      email: user.email || '',
      phone: user.phone || store?.phone || '',
      whatsappNumber: store?.whatsappNumber || '',
      address: store?.address || '',
      city: store?.city || '',
      state: store?.state || '',
      zipCode: store?.zipCode || '',
      country: store?.country || '',
    });
  }, [user, store]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const axios = getAxios();
      const profileResponse = await axios.put('merchant/auth/profile', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        email: formData.email,
        phone: formData.phone,
      });

      if (store && store.id) {
        await dispatch(
          updateStore({
            payload: {
              phone: formData.phone,
              whatsappNumber: formData.whatsappNumber,
              address: formData.address,
              city: formData.city,
              state: formData.state,
              zipCode: formData.zipCode,
              country: formData.country,
            },
            axiosInstance: axios,
          }),
        ).unwrap();
      }

      await refreshUser();

      if (profileResponse.data.emailChanged) {
        setEmailVerificationPending(true);
        setPendingEmail(profileResponse.data.newEmail);
        setIsEditing(false);
        toast.warning('Profile updated! Please verify your new email address.');
      } else {
        setIsEditing(false);
        toast.success('Profile updated successfully!');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error('Failed to update profile: ' + (err.error || err.message || 'Unknown error'));
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      username: user?.username || '',
      email: user?.email || '',
      phone: user?.phone || store?.phone || '',
      whatsappNumber: store?.whatsappNumber || '',
      address: store?.address || '',
      city: store?.city || '',
      state: store?.state || '',
      zipCode: store?.zipCode || '',
      country: store?.country || '',
    });
    setIsEditing(false);
  };

  const handlePasswordSave = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    try {
      const axios = getAxios();
      await axios.put('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password updated successfully');
    } catch (err) {
      console.error('Error changing password:', err);
      toast.error('Failed to change password: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleResendVerification = async () => {
    try {
      const axios = getAxios();
      await axios.post('/auth/resend-verification', { newEmail: pendingEmail });
      toast.success('Verification email has been resent');
    } catch (err) {
      console.error('Error resending verification:', err);
      toast.error(err.response?.data?.error || 'Failed to resend verification email');
    }
  };

  const copyMerchantCode = () => {
    if (!user?.merchantCode) return;
    navigator.clipboard?.writeText(user.merchantCode).then(
      () => toast.success('Merchant ID copied'),
      () => toast.error('Could not copy — copy manually'),
    );
  };

  const initials = getInitials(user?.firstName, user?.lastName, user?.username, user?.email);
  const canChangePassword = passwordData.currentPassword && passwordData.newPassword && passwordData.confirmPassword;

  return (
    <Shell>
      <div className={`pr-page${isEditing ? ' pr-editing' : ''}`}>
        {/* Header */}
        <header className="pr-header">
          <div className="pr-avatar" aria-hidden="true">{initials}</div>
          <div className="pr-header-text">
            <h1 className="pr-title">Profile</h1>
            <p className="pr-subtitle">Manage your personal information and account details.</p>
          </div>
        </header>

        {/* Error banner */}
        {error && (
          <div className="pr-banner pr-banner-error" role="alert">
            <div className="pr-banner-row">
              <AlertTriangle size={14} aria-hidden="true" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Email verification banner */}
        {emailVerificationPending && (
          <div className="pr-banner pr-banner-warn" role="alert">
            <div className="pr-banner-row">
              <Mail size={14} aria-hidden="true" />
              <span className="pr-banner-title">Email verification required</span>
            </div>
            <p className="pr-banner-text">
              We've sent a verification email to <strong>{pendingEmail}</strong>. Click the link in your inbox to complete the change.
            </p>
            <div className="pr-banner-actions">
              <button type="button" className="pr-banner-btn pr-banner-btn-primary" onClick={handleResendVerification}>
                Resend email
              </button>
              <button type="button" className="pr-banner-btn pr-banner-btn-ghost" onClick={() => setEmailVerificationPending(false)}>
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Personal information */}
        <section className="pr-card">
          <div className="pr-card-body">
            <div className="pr-card-head">
              <div className="pr-card-head-text">
                <h2 className="pr-card-title">Personal information</h2>
                {user?.merchantCode && (
                  <div className="pr-meta">
                    <span>Merchant ID:</span>
                    <span className="pr-meta-mono">{user.merchantCode}</span>
                    <button
                      type="button"
                      className="pr-meta-copy"
                      onClick={copyMerchantCode}
                      title="Copy Merchant ID"
                      aria-label="Copy Merchant ID"
                    >
                      <Copy size={12} aria-hidden="true" />
                    </button>
                  </div>
                )}
              </div>
              <div className="pr-card-actions">
                {!isEditing && (
                  <button type="button" className="pr-btn pr-btn-secondary" onClick={() => setIsEditing(true)}>
                    <Edit2 size={14} aria-hidden="true" />
                    <span>Edit</span>
                  </button>
                )}
              </div>
            </div>

            <div className="pr-grid pr-grid-2col">
              <label className="pr-field">
                <span className="pr-field-label">First name</span>
                <input
                  className="pr-input"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Your first name"
                />
              </label>
              <label className="pr-field">
                <span className="pr-field-label">Last name</span>
                <input
                  className="pr-input"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Your last name"
                />
              </label>
              <label className="pr-field">
                <span className="pr-field-label">Username</span>
                <input
                  className="pr-input"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Your username"
                />
              </label>
              <label className="pr-field">
                <span className="pr-field-label">Email</span>
                <input
                  className="pr-input"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="you@example.com"
                />
              </label>
              <label className="pr-field">
                <span className="pr-field-label">Phone</span>
                <input
                  className="pr-input"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="+234 XXX XXX XXXX"
                />
              </label>
              <label className="pr-field">
                <span className="pr-field-label">WhatsApp number</span>
                <input
                  className="pr-input"
                  name="whatsappNumber"
                  value={formData.whatsappNumber}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="+234 XXX XXX XXXX"
                />
              </label>
              <label className="pr-field">
                <span className="pr-field-label">Country</span>
                <input
                  className="pr-input"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Nigeria"
                />
              </label>
            </div>
          </div>
        </section>

        {/* Address */}
        <section className="pr-card">
          <div className="pr-card-body">
            <div className="pr-card-head">
              <div className="pr-card-head-text">
                <h2 className="pr-card-title">Address</h2>
              </div>
            </div>

            <div className="pr-grid">
              <label className="pr-field">
                <span className="pr-field-label">Street address</span>
                <input
                  className="pr-input"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="123 Main Street"
                />
              </label>
              <div className="pr-grid pr-grid-3col">
                <label className="pr-field">
                  <span className="pr-field-label">City</span>
                  <input
                    className="pr-input"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Lagos"
                  />
                </label>
                <label className="pr-field">
                  <span className="pr-field-label">State / Province</span>
                  <input
                    className="pr-input"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Lagos"
                  />
                </label>
                <label className="pr-field">
                  <span className="pr-field-label">ZIP / Postal code</span>
                  <input
                    className="pr-input"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="100001"
                  />
                </label>
              </div>
            </div>
          </div>
        </section>

        {/* Change password */}
        <section className="pr-card">
          <div className="pr-card-body">
            <div className="pr-card-head">
              <div className="pr-card-head-text">
                <h2 className="pr-card-title">Change password</h2>
              </div>
            </div>

            <div className="pr-grid">
              <label className="pr-field">
                <span className="pr-field-label">Current password</span>
                <input
                  className="pr-input"
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                />
              </label>
              <div className="pr-grid pr-grid-2col">
                <label className="pr-field">
                  <span className="pr-field-label">New password</span>
                  <input
                    className="pr-input"
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                  />
                </label>
                <label className="pr-field">
                  <span className="pr-field-label">Confirm new password</span>
                  <input
                    className="pr-input"
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                  />
                </label>
              </div>
            </div>

            <div className="pr-card-foot">
              <button
                type="button"
                className="pr-btn pr-btn-primary"
                onClick={handlePasswordSave}
                disabled={!canChangePassword || status === 'loading'}
              >
                {status === 'loading' ? 'Updating…' : 'Update password'}
              </button>
            </div>
          </div>
        </section>

        {/* Floating edit action bar — only visible during edit mode.
            Pinned to the bottom of the viewport so Save/Cancel are
            always within reach regardless of scroll. */}
        {isEditing && (
          <div className="pr-edit-bar" role="region" aria-label="Edit actions">
            <button type="button" className="pr-btn pr-btn-ghost" onClick={handleCancel}>
              <X size={14} aria-hidden="true" />
              <span>Cancel</span>
            </button>
            <button type="button" className="pr-btn pr-btn-primary" onClick={handleSave} disabled={status === 'loading'}>
              <Check size={14} aria-hidden="true" />
              <span>{status === 'loading' ? 'Saving…' : 'Save changes'}</span>
            </button>
          </div>
        )}
      </div>
    </Shell>
  );
}
